import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowTrendUp,
  faCashRegister,
  faChartLine,
  faMoneyBillTrendUp,
  faReceipt,
  faRankingStar,
  faTrophy,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import GetSpace from "../../utils/space"
import { useTranslation } from "react-i18next"
import { useGetOrder } from "../../hooks/Order/useGetOrder"
import { DailyCalculate } from "../../Objects/Calculates/DailyCalculate"
import { IEmployeePerformanceMetric } from "../../Interfaces/IEmployeePerformanceMetric"
import { convertToLocalMoney, fromInputDate } from "../../Objects/Generics/Helps"
import { useLocation } from "react-router-dom"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import { companyMemory } from "../../Objects/Memory/Memory"
import { getRestaurantNow } from "../../utils/timeZone"
import { CashFlowPeriodFilterPopup } from "./CashFlowPeriodFilterPopup"
import { CashFlowMetricsLoading } from "./CashFlowMetricsLoading"
import {
  buildCompactLabelValueListHtml,
  buildCompactSectionHtml,
  buildSummaryCardsHtml,
  buildTableSectionHtml,
  escapeHtml,
  openPrintReport,
} from "../../utils/printReport"
import {
  buildCashFlowMetricsCacheKey,
  deleteCashFlowMetricsCache,
  getCashFlowMetricsCache,
  setCashFlowMetricsCache,
} from "./CashFlowMetricsCache"
import { loadCashFlowOrders } from "./loadCashFlowOrders"
import { FinancialPageLayout } from "./components/FinancialPageLayout"
import { FinancialPageHeader } from "./components/FinancialPageHeader"
import { FinancialKpiCard as MetricCard } from "./components/FinancialKpiCard"
import { FinancialChartCard } from "./components/FinancialChartCard"
import { FinancialHorizontalBarChart } from "./components/charts/FinancialHorizontalBarChart"
import { FinancialDataTable } from "./components/FinancialDataTable"
import type { FinancialTableColumn } from "./types/financialUi"
import { limitFinancialRanking } from "./utils/financialChartUtils"
import { CashFlowPrintFormatPopup, type CashFlowPrintFormat } from "./CashFlowPrintFormatPopup"

const getEmployeeLabel = (employee: IEmployeePerformanceMetric | null, t: (key: string) => string) => {
  if (!employee) {
    return t("cashflow.no_employee_data")
  }

  if (employee.companyFunction) {
    return `${employee.employeeName} • ${employee.companyFunction}`
  }

  return employee.employeeName
}

interface EmployeeInsightProps {
  title: string
  value: string
  subtitle: string
  helpDescription: string
  helpBullets: string[]
}

const EmployeeInsightCard: React.FC<EmployeeInsightProps> = ({
  title,
  value,
  subtitle,
  helpDescription,
  helpBullets,
}) => (
  <Animate type={AnimationTypes.fadeInTraslate}>
    <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />

      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
        <PopupHelpButton
          helpKey="pages.cashflow.employeemetricspage.1"
          title={title}
          description={helpDescription}
          bullets={helpBullets}
        />

        <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500 shadow-sm">
          <FontAwesomeIcon icon={faRankingStar} />
        </span>

        <span>{title}</span>
      </div>

      <div className="mt-4 text-xl font-black leading-tight text-slate-800">{value}</div>

      <p className="mt-2 text-xs leading-5 text-slate-500">{subtitle}</p>
    </div>
  </Animate>
)

export const EmployeeMetricsPage: React.FC = () => {
  const { t } = useTranslation("pages")
  
  const getHelpBullets = (key: string) => {
    const translated = t(key, { returnObjects: true })
    return Array.isArray(translated) ? translated.map((item) => String(item)) : []
  }

  const location = useLocation()

  const [today] = useState(() => getRestaurantNow(companyMemory.timeZoneId))

  const [dateStart, setDateStart] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toInputDate())

  const [dateFinal, setDateFinal] = useState(today.toInputDate())

  const [showPeriodFilter, setShowPeriodFilter] = useState(false)

  const [showPrintFormatPopup, setShowPrintFormatPopup] = useState(false)

  const [calculate, setCalculate] = useState<DailyCalculate>(new DailyCalculate())
  
  const [isMetricsLoading, setIsMetricsLoading] = useState(false)
  
  const [refreshNonce, setRefreshNonce] = useState(0)

  const { handleGetOrder } = useGetOrder()

  const handleGetOrderRef = useRef(handleGetOrder)

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
  }, [handleGetOrder])

  useEffect(() => {
    let isMounted = true

    const runCalculate = async () => {
      const cacheKey = buildCashFlowMetricsCacheKey("employee-metrics", dateStart, dateFinal)
     
      const cachedCalculate = getCashFlowMetricsCache<DailyCalculate>(cacheKey)

      if (cachedCalculate) {
        setCalculate(cachedCalculate)
        return
      }

      setIsMetricsLoading(true)

      try {
        await loadCashFlowOrders(handleGetOrderRef.current, dateStart, dateFinal)

        if (!isMounted) {
          return
        }

        const nextCalculate = new DailyCalculate()

        nextCalculate.calculate()

        setCalculate(nextCalculate)
        setCashFlowMetricsCache(cacheKey, nextCalculate)
      } finally {
        if (isMounted) {
          setIsMetricsLoading(false)
        }
      }
    }

    void runCalculate()

    return () => {
      isMounted = false
    }
  }, [dateFinal, dateStart, location.pathname, refreshNonce])

  const topEmployeeByRevenue = calculate.employeePerformance[0] ?? null

  const topEmployeeByOrders = useMemo(
    () =>
      [...calculate.employeePerformance].sort((a, b) => {
        if (b.ordersCount !== a.ordersCount) {
          return b.ordersCount - a.ordersCount
        }

        return b.netRevenue - a.netRevenue
      })[0] ?? null,
    [calculate.employeePerformance],
  )

  const topEmployeeByAverageTicket = useMemo(
    () =>
      calculate.employeePerformance
        .filter((item) => item.closedOrdersCount > 0)
        .sort((a, b) => b.averageTicket - a.averageTicket)[0] ?? null,
    [calculate.employeePerformance],
  )

  const monitoredEmployeesCount = calculate.employeePerformance.length

  const totalTrackedOrders = calculate.employeePerformance.reduce((sum, item) => sum + item.ordersCount, 0)

  const totalTrackedClosedOrders = calculate.employeePerformance.reduce((sum, item) => sum + item.closedOrdersCount, 0)

  const totalTrackedRevenue = calculate.employeePerformance.reduce((sum, item) => sum + item.netRevenue, 0)

  const overallAverageTicket = totalTrackedClosedOrders > 0 ? totalTrackedRevenue / totalTrackedClosedOrders : 0

  const handleApplyPeriodFilter = (filters: { dateStart: string; dateEnd: string }, forceRefresh?: boolean) => {
    if (forceRefresh) {
      deleteCashFlowMetricsCache(buildCashFlowMetricsCacheKey("employee-metrics", filters.dateStart, filters.dateEnd))

      if (filters.dateStart === dateStart && filters.dateEnd === dateFinal) {
        setRefreshNonce((currentValue) => currentValue + 1)
      }
    }

    setDateStart(filters.dateStart)
    setDateFinal(filters.dateEnd)
    setShowPeriodFilter(false)
  }

  const handlePrintReport = (format: CashFlowPrintFormat) => {
    const summaryCards = [
      { label: t("cashflow.employees_monitored_title"), value: monitoredEmployeesCount.toString() },
      { label: t("cashflow.total_orders_tracked"), value: totalTrackedOrders.toString() },
      { label: t("cashflow.total_employee_revenue"), value: convertToLocalMoney(totalTrackedRevenue) },
      { label: t("cashflow.average_ticket_tracked"), value: convertToLocalMoney(overallAverageTicket) },
      {
        label: t("cashflow.top_employee_revenue"),
        value: getEmployeeLabel(topEmployeeByRevenue, t),
        subtitle: topEmployeeByRevenue
          ? `${convertToLocalMoney(topEmployeeByRevenue.netRevenue)} • ${topEmployeeByRevenue.closedOrdersCount} ${t("cashflow.closed_count").toLowerCase()}`
          : t("cashflow.no_employee_data_description"),
      },
      {
        label: t("cashflow.top_employee_orders"),
        value: getEmployeeLabel(topEmployeeByOrders, t),
        subtitle: topEmployeeByOrders
          ? `${topEmployeeByOrders.ordersCount} ${t("cashflow.total_orders").toLowerCase()}`
          : t("cashflow.no_employee_data_description"),
      },
      {
        label: t("cashflow.top_employee_ticket"),
        value: getEmployeeLabel(topEmployeeByAverageTicket, t),
        subtitle: topEmployeeByAverageTicket
          ? `${t("cashflow.average_ticket")}: ${convertToLocalMoney(topEmployeeByAverageTicket.averageTicket)}`
          : t("cashflow.no_employee_data_description"),
      },
    ]

    const rankingHtml = buildTableSectionHtml<IEmployeePerformanceMetric>(
      t("cashflow.employee_ranking_title"),
      [
        { header: t("cashflow.employee_column"), render: (row) => escapeHtml(row.employeeName) },
        { header: t("cashflow.orders_column"), render: (row) => row.ordersCount.toString() },
        { header: t("cashflow.channel_local"), render: (row) => row.localOrdersCount.toString() },
        { header: t("cashflow.channel_delivery"), render: (row) => row.deliveryOrdersCount.toString() },
        { header: t("cashflow.closed_column"), render: (row) => row.closedOrdersCount.toString() },
        { header: t("cashflow.canceled_column"), render: (row) => row.canceledOrdersCount.toString() },
        { header: t("cashflow.items_column"), render: (row) => row.totalItemsProduced.toString() },
        { header: t("cashflow.net_revenue_column"), render: (row) => escapeHtml(convertToLocalMoney(row.netRevenue)) },
        {
          header: t("cashflow.gross_revenue_column"),
          render: (row) => escapeHtml(convertToLocalMoney(row.grossRevenue)),
        },
        {
          header: t("cashflow.average_ticket_column"),
          render: (row) => escapeHtml(convertToLocalMoney(row.averageTicket)),
        },
      ],
      calculate.employeePerformance,
      t("cashflow.no_employee_data_description"),
    )

    const employeeRows = calculate.employeePerformance.slice(0, 12).map((employee) => ({
      label: getEmployeeLabel(employee, t),
      value: convertToLocalMoney(employee.netRevenue),
      subtitle: `${t("cashflow.orders_column")}: ${employee.ordersCount} • ${t("cashflow.closed_column")}: ${employee.closedOrdersCount} • ${t("cashflow.average_ticket_column")}: ${convertToLocalMoney(employee.averageTicket)}`,
    }))

    const bodyHtml =
      format === "thermal"
        ? [
            buildCompactLabelValueListHtml(summaryCards),
            buildCompactSectionHtml(t("cashflow.employee_ranking_title"), employeeRows),
          ].join("")
        : [buildSummaryCardsHtml(summaryCards), rankingHtml].join("")

    openPrintReport({
      title: t("cashflow.employee_metrics_title"),
      subtitle: [
        companyMemory.companyName || t("cashflow.print_report.restaurant_fallback"),
        t("cashflow.print_report.period_label", {
          startDate: fromInputDate(dateStart).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(dateFinal).toLocaleDateString("pt-BR"),
        }),
        t("cashflow.print_report.generated_at_label", {
          dateTime: getRestaurantNow(companyMemory.timeZoneId).toLocaleDateTimeOrDash(
            "pt-BR",
            companyMemory.timeZoneId,
          ),
        }),
      ].join(" • "),
      bodyHtml,
      format,
    })
  }

  const handleSelectPrintFormat = (format: CashFlowPrintFormat) => {
    setShowPrintFormatPopup(false)
    handlePrintReport(format)
  }

  const employeeRevenueChartData = limitFinancialRanking(
    calculate.employeePerformance
      .filter((employee) => employee.closedOrdersCount > 0)
      .map((employee) => ({ label: employee.employeeName, value: employee.netRevenue })),
  )

  const employeeColumns = useMemo<FinancialTableColumn<IEmployeePerformanceMetric>[]>(
    () => [
      {
        id: "employee",
        label: t("cashflow.employee_column"),
        sortValue: (employee) => employee.employeeName,
        mobilePriority: "primary",
        render: (employee) => <span className="font-extrabold text-slate-900">{employee.employeeName}</span>,
      },
      {
        id: "orders",
        label: t("cashflow.orders_column"),
        align: "right",
        sortValue: (employee) => employee.ordersCount,
        mobilePriority: "summary",
        render: (employee) => employee.ordersCount,
      },
      {
        id: "local",
        label: t("cashflow.channel_local"),
        align: "right",
        sortValue: (employee) => employee.localOrdersCount,
        render: (employee) => employee.localOrdersCount,
      },
      {
        id: "delivery",
        label: t("cashflow.channel_delivery"),
        align: "right",
        sortValue: (employee) => employee.deliveryOrdersCount,
        render: (employee) => employee.deliveryOrdersCount,
      },
      {
        id: "closed",
        label: t("cashflow.closed_column"),
        align: "right",
        sortValue: (employee) => employee.closedOrdersCount,
        render: (employee) => employee.closedOrdersCount,
      },
      {
        id: "canceled",
        label: t("cashflow.canceled_column"),
        align: "right",
        sortValue: (employee) => employee.canceledOrdersCount,
        render: (employee) => employee.canceledOrdersCount,
      },
      {
        id: "items",
        label: t("cashflow.items_column"),
        align: "right",
        sortValue: (employee) => employee.totalItemsProduced,
        render: (employee) => employee.totalItemsProduced,
      },
      {
        id: "netRevenue",
        label: t("cashflow.net_revenue_column"),
        align: "right",
        sortValue: (employee) => employee.netRevenue,
        mobilePriority: "summary",
        render: (employee) => <span className="font-extrabold text-emerald-700">{convertToLocalMoney(employee.netRevenue)}</span>,
      },
      {
        id: "grossRevenue",
        label: t("cashflow.gross_revenue_column"),
        align: "right",
        sortValue: (employee) => employee.grossRevenue,
        render: (employee) => convertToLocalMoney(employee.grossRevenue),
      },
      {
        id: "averageTicket",
        label: t("cashflow.average_ticket_column"),
        align: "right",
        sortValue: (employee) => employee.averageTicket,
        render: (employee) => convertToLocalMoney(employee.averageTicket),
      },
    ],
    [t],
  )

  const paginationLabels = {
    ariaLabel: t("cashflow.table_pagination_aria"),
    previous: t("cashflow.pagination_previous"),
    next: t("cashflow.pagination_next"),
    page: (current: number, total: number) => t("cashflow.pagination_page", { current, total }),
    range: (first: number, last: number, total: number) => t("cashflow.pagination_range", { first, last, total }),
    itemsPerPage: t("cashflow.pagination_size"),
  }

  return (
    <FinancialPageLayout>
      {showPeriodFilter && (
        <CashFlowPeriodFilterPopup
          initialFilters={{ dateStart, dateEnd: dateFinal }}
          today={today}
          onApplyFilters={handleApplyPeriodFilter}
          onClose={() => setShowPeriodFilter(false)}
        />
      )}

      {showPrintFormatPopup && (
        <CashFlowPrintFormatPopup
          onSelectFormat={handleSelectPrintFormat}
          onClose={() => setShowPrintFormatPopup(false)}
        />
      )}

      <FinancialPageHeader
        title={t("cashflow.employee_metrics_title")}
        subtitle={t("cashflow.employee_page_subtitle")}
        period={t("cashflow.print_report.period_label", {
          startDate: fromInputDate(dateStart).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(dateFinal).toLocaleDateString("pt-BR"),
        })}
        filterLabel={t("cashflow.filter_action")}
        printLabel={t("cashflow.print_action")}
        isLoading={isMetricsLoading}
        onFilter={() => setShowPeriodFilter(true)}
        onPrint={() => setShowPrintFormatPopup(true)}
      />

      <div>
        <CashFlowMetricsLoading isVisible={isMetricsLoading} />

        {!isMetricsLoading && (
          <>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            title={t("cashflow.employees_monitored_title")}
            value={monitoredEmployeesCount.toString()}
            icon={faUsers}
            tone="info"
            helpDescription={t("cashflow.help.employees_monitored.description")}
            helpBullets={getHelpBullets("cashflow.help.employees_monitored.bullets")}
          />

          <MetricCard
            title={t("cashflow.total_orders_tracked")}
            value={totalTrackedOrders.toString()}
            icon={faReceipt}
            helpDescription={t("cashflow.help.total_orders_tracked.description")}
            helpBullets={getHelpBullets("cashflow.help.total_orders_tracked.bullets")}
          />

          <MetricCard
            title={t("cashflow.total_employee_revenue")}
            value={convertToLocalMoney(totalTrackedRevenue)}
            icon={faMoneyBillTrendUp}
            tone="positive"
            helpDescription={t("cashflow.help.total_employee_revenue.description")}
            helpBullets={getHelpBullets("cashflow.help.total_employee_revenue.bullets")}
          />

          <MetricCard
            title={t("cashflow.average_ticket_tracked")}
            value={convertToLocalMoney(overallAverageTicket)}
            icon={faCashRegister}
            helpDescription={t("cashflow.help.average_ticket_tracked.description")}
            helpBullets={getHelpBullets("cashflow.help.average_ticket_tracked.bullets")}
          />
        </div>

        <GetSpace top={10} />

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <EmployeeInsightCard
            title={t("cashflow.top_employee_revenue")}
            value={getEmployeeLabel(topEmployeeByRevenue, t)}
            subtitle={
              topEmployeeByRevenue
                ? `${convertToLocalMoney(topEmployeeByRevenue.netRevenue)} • ${topEmployeeByRevenue.closedOrdersCount} ${t("cashflow.closed_count").toLowerCase()}`
                : t("cashflow.no_employee_data_description")
            }
            helpDescription={t("cashflow.help.top_employee_revenue.description")}
            helpBullets={getHelpBullets("cashflow.help.top_employee_revenue.bullets")}
          />

          <EmployeeInsightCard
            title={t("cashflow.top_employee_orders")}
            value={getEmployeeLabel(topEmployeeByOrders, t)}
            subtitle={
              topEmployeeByOrders
                ? `${topEmployeeByOrders.ordersCount} ${t("cashflow.total_orders").toLowerCase()}`
                : t("cashflow.no_employee_data_description")
            }
            helpDescription={t("cashflow.help.top_employee_orders.description")}
            helpBullets={getHelpBullets("cashflow.help.top_employee_orders.bullets")}
          />

          <EmployeeInsightCard
            title={t("cashflow.top_employee_ticket")}
            value={getEmployeeLabel(topEmployeeByAverageTicket, t)}
            subtitle={
              topEmployeeByAverageTicket
                ? `${t("cashflow.average_ticket")}: ${convertToLocalMoney(topEmployeeByAverageTicket.averageTicket)}`
                : t("cashflow.no_employee_data_description")
            }
            helpDescription={t("cashflow.help.top_employee_ticket.description")}
            helpBullets={getHelpBullets("cashflow.help.top_employee_ticket.bullets")}
          />
        </div>

        <GetSpace top={10} />

        <Animate type={AnimationTypes.fadeInTraslate}>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-amber-600 shadow-sm">
                  <FontAwesomeIcon icon={faTrophy} />
                </span>

                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    {t("cashflow.employee_best_revenue")}
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-800">
                    {getEmployeeLabel(topEmployeeByRevenue, t)}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-blue-600 shadow-sm">
                  <FontAwesomeIcon icon={faChartLine} />
                </span>

                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    {t("cashflow.employee_best_ticket")}
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-800">
                    {topEmployeeByAverageTicket
                      ? convertToLocalMoney(topEmployeeByAverageTicket.averageTicket)
                      : t("cashflow.no_employee_data")}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-emerald-600 shadow-sm">
                  <FontAwesomeIcon icon={faArrowTrendUp} />
                </span>

                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    {t("cashflow.employee_best_volume")}
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-800">
                    {getEmployeeLabel(topEmployeeByOrders, t)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Animate>

        <GetSpace top={10} />

        <FinancialChartCard
          title={t("cashflow.employee_revenue_chart_title")}
          subtitle={t("cashflow.employee_revenue_chart_subtitle")}
        >
          <FinancialHorizontalBarChart
            data={employeeRevenueChartData}
            ariaLabel={t("cashflow.employee_revenue_chart_title")}
            emptyTitle={t("cashflow.no_employee_data")}
            emptyDescription={t("cashflow.no_employee_data_description")}
            formatValue={convertToLocalMoney}
            valueLabel={t("cashflow.net_revenue_column")}
          />
        </FinancialChartCard>

        <FinancialDataTable
          title={t("cashflow.employee_ranking_title")}
          subtitle={t("cashflow.employee_ranking_subtitle")}
          rows={calculate.employeePerformance}
          getRowKey={(employee) => employee.employeeId}
          columns={employeeColumns}
          searchText={(employee) => `${employee.employeeName} ${employee.companyFunction ?? ""}`}
          searchLabel={t("cashflow.employee_search")}
          emptyTitle={t("cashflow.no_employee_data")}
          emptyDescription={t("cashflow.no_employee_data_description")}
          initialSort={{ columnId: "netRevenue", direction: "descending" }}
          paginationLabels={paginationLabels}
        />
          </>
        )}
      </div>
    </FinancialPageLayout>
  )
}
